import type { Essay, FavoriteItem, GrammarError, ReviewResult } from "../types";

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
}

export interface PublicAppConfig {
  inviteOnlySignup: boolean;
}

export interface AdminUserSummary {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
  createdAt: string;
  updatedAt: string;
  essayCount: number;
  favoriteCount: number;
}

export interface AdminUserDetail {
  user: AppUser;
  essays: Essay[];
  favorites: FavoriteItem[];
}

export interface AdminExportPayload {
  generatedAt: string;
  totals: {
    users: number;
    essays: number;
    favorites: number;
  };
  users: Array<{
    user: AdminUserSummary;
    essays: Essay[];
    favorites: FavoriteItem[];
  }>;
}

async function apiFetch(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(path, {
    ...init,
    headers,
    credentials: "include",
  });
}

async function parseError(res: Response, fallback: string) {
  const errorData = await res.json().catch(() => ({ error: fallback }));
  throw new Error((errorData as { error?: string }).error || fallback);
}

export const client = {
  auth: {
    async getSession(): Promise<{ data: { user: AppUser } | null }> {
      const res = await apiFetch("/api/auth/session");
      if (!res.ok) {
        return { data: null };
      }

      const data = (await res.json()) as { data: { user: AppUser } | null };
      return data;
    },

    async getPublicAppConfig(): Promise<{ data: PublicAppConfig }> {
      const res = await apiFetch("/api/public/app-config");
      if (!res.ok) {
        return { data: { inviteOnlySignup: true } };
      }

      return (await res.json()) as { data: PublicAppConfig };
    },

    async signIn(email: string, password: string): Promise<{ data: { user: AppUser } }> {
      const res = await apiFetch("/api/auth/sign-in", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        await parseError(res, "登录失败");
      }

      return (await res.json()) as { data: { user: AppUser } };
    },

    async signUp(email: string, password: string, name?: string, inviteCode?: string): Promise<{ data: { user: AppUser } }> {
      const res = await apiFetch("/api/auth/sign-up", {
        method: "POST",
        body: JSON.stringify({ email, password, name, inviteCode }),
      });

      if (!res.ok) {
        await parseError(res, "注册失败");
      }

      return (await res.json()) as { data: { user: AppUser } };
    },

    async signOut(): Promise<{ success: boolean }> {
      const res = await apiFetch("/api/auth/sign-out", {
        method: "POST",
      });

      if (!res.ok) {
        await parseError(res, "退出登录失败");
      }

      return (await res.json()) as { success: boolean };
    },
  },
};

export async function analyzeEssay(title: string, content: string) {
  const res = await apiFetch("/api/public/analyze", {
    method: "POST",
    body: JSON.stringify({ title, content }),
  });

  if (!res.ok) {
    await parseError(res, `分析失败（${res.status}）`);
  }

  const data = (await res.json()) as { errors: any[] };
  return data.errors;
}

export async function saveEssayToBackend(title: string, content: string, errors: GrammarError[]) {
  const res = await apiFetch("/api/essays", {
    method: "POST",
    body: JSON.stringify({ title, content, errors }),
  });

  if (!res.ok) {
    await parseError(res, `保存失败（${res.status}）`);
  }

  return (await res.json()) as { data: Essay };
}

export async function fetchFavoritesFromBackend(): Promise<FavoriteItem[]> {
  const res = await apiFetch("/api/favorites");

  if (!res.ok) {
    await parseError(res, `获取错题集失败（${res.status}）`);
  }

  const data = (await res.json()) as { data: FavoriteItem[] };
  return data.data;
}

export async function addFavoriteToBackend(
  errorId: number | string,
  initialReviewResult?: ReviewResult | null
) {
  const res = await apiFetch("/api/favorites", {
    method: "POST",
    body: JSON.stringify({ errorId, initialReviewResult }),
  });

  if (!res.ok) {
    await parseError(res, `收藏失败（${res.status}）`);
  }

  return (await res.json()) as { data: FavoriteItem };
}

export async function removeFavoriteFromBackend(errorId: number | string) {
  const res = await apiFetch(`/api/favorites/${errorId}`, {
    method: "DELETE",
  });

  if (!res.ok) {
    await parseError(res, `移除收藏失败（${res.status}）`);
  }

  return (await res.json()) as { success: boolean };
}

export async function reviewFavoriteInBackend(errorId: number | string, result: ReviewResult) {
  const res = await apiFetch(`/api/favorites/${errorId}/review`, {
    method: "PATCH",
    body: JSON.stringify({ result }),
  });

  if (!res.ok) {
    await parseError(res, `更新复习进度失败（${res.status}）`);
  }

  return (await res.json()) as { data: FavoriteItem };
}

export async function updateEssayErrorInBackend(
  essayId: number | string,
  errorId: number | string,
  updates: Pick<GrammarError, "correctedSentence" | "explanationZh">
) {
  const res = await apiFetch(`/api/essays/${essayId}/errors/${errorId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    await parseError(res, `更新失败（${res.status}）`);
  }

  return (await res.json()) as { data: GrammarError };
}

export async function deleteEssayFromBackend(essayId: number | string) {
  const res = await apiFetch(`/api/essays/${essayId}`, {
    method: "DELETE",
  });

  if (!res.ok) {
    await parseError(res, `删除失败（${res.status}）`);
  }

  return (await res.json()) as { success: boolean };
}

export async function fetchUserEssays(): Promise<Essay[]> {
  const res = await apiFetch("/api/essays");

  if (!res.ok) {
    await parseError(res, `获取作文失败（${res.status}）`);
  }

  const data = (await res.json()) as { data: Essay[] };
  return data.data;
}

export async function fetchAdminUsers() {
  const res = await apiFetch("/api/admin/users");

  if (!res.ok) {
    await parseError(res, `获取管理员数据失败（${res.status}）`);
  }

  return (await res.json()) as {
    data: {
      users: AdminUserSummary[];
      totals: {
        users: number;
        essays: number;
        favorites: number;
      };
    };
  };
}

export async function fetchAdminUserDetail(userId: string) {
  const res = await apiFetch(`/api/admin/users/${userId}`);

  if (!res.ok) {
    await parseError(res, `获取用户详情失败（${res.status}）`);
  }

  return (await res.json()) as { data: AdminUserDetail };
}

export async function fetchAdminExport() {
  const res = await apiFetch("/api/admin/export");

  if (!res.ok) {
    await parseError(res, `导出全站数据失败（${res.status}）`);
  }

  return (await res.json()) as { data: AdminExportPayload };
}

export async function adminDeleteEssay(userId: string, essayId: number | string) {
  const res = await apiFetch(`/api/admin/users/${userId}/essays/${essayId}`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    await parseError(res, `删除作文失败（${res.status}）`);
  }

  return (await res.json()) as { success: boolean };
}

export async function adminUpdateEssayError(
  userId: string,
  essayId: number | string,
  errorId: number | string,
  updates: Pick<GrammarError, 'correctedSentence' | 'explanationZh'>
) {
  const res = await apiFetch(`/api/admin/users/${userId}/essays/${essayId}/errors/${errorId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    await parseError(res, `更新分析结果失败（${res.status}）`);
  }

  return (await res.json()) as { data: GrammarError };
}

export async function adminDeleteEssayError(userId: string, essayId: number | string, errorId: number | string) {
  const res = await apiFetch(`/api/admin/users/${userId}/essays/${essayId}/errors/${errorId}`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    await parseError(res, `删除分析结果失败（${res.status}）`);
  }

  return (await res.json()) as { success: boolean };
}

export async function adminAddFavorite(userId: string, errorId: number | string) {
  const res = await apiFetch(`/api/admin/users/${userId}/favorites`, {
    method: 'POST',
    body: JSON.stringify({ errorId }),
  });

  if (!res.ok) {
    await parseError(res, `加入错题集失败（${res.status}）`);
  }

  return (await res.json()) as { data: FavoriteItem };
}
